import {Injectable} from "@angular/core";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import gql from "graphql-tag";
import {NgRedux, select} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store/store";
import {userFragments} from "../../../crm/user/user.service";
import {AdministrationUnit} from "../../../shared/view-model/organization/administration-unit.type";
import {addressFragments} from "../../../core/visbos-core/services/address.service";
import {Observable} from "rxjs/Observable";
import {VisbosService} from "../../../core/visbos-core/graphql/visbos.service";
import {stockFragments} from "../../../store-management/services/stock.service";
import {SessionActions} from "../../../shared/actions";
import {map} from "rxjs/operators";
import {FetchAdminUnitsEANOverviewQueryQuery} from '../../../shared/graphql/graphql.types';

export const businessRelationFragments = {
  businessRelation: gql`
    fragment BusinessRelationFields on BusinessRelation {
      cuid, label, is_editable
      relation_source {
        cuid, label
      }
      relation_target {
        cuid, label
      }
      relation_type {
        cuid, label
        ...on SupplierBusinessRelation {
          order_kind_label,
          customer_number,
          order_data {
            cuid, label, is_editable
            email, phone, fax
          }
        }
      }
    }
  `
};

export const adminUnitFragments = {
  adminUnit: gql`
    fragment AdminUnitDetailFields on AdministrationUnit {
      cuid, label, is_editable
      name, address_name
      address {
        ...AddressFields
      }
      billing_address {
        ...AddressFields
      }
      business_relations {
        ...BusinessRelationFields
      }
      is_supplier { cuid label }
      forced_supplier {cuid label }
      is_manager_of_external_depots
      is_central_depot
      manages_external_depots {
        cuid label
        administration_unit {cuid label}
      }
      is_managed_by_external_admin_units
      depots_managed_by_external_admin_unit {
        cuid label
        administration_unit {cuid label}
      }
    }

    ${addressFragments.address}
    ${businessRelationFragments.businessRelation}
  `,
  adminUnitOverview: gql`
    fragment AdminUnitFields on AdministrationUnit {
      cuid, label, is_editable
      name, is_central_depot
    }
  `
};

export const fetchUserQuery = gql`
  query FetchUserQuery($cuid: String) {
    fetchUsers(cuid: $cuid) {
      ...UserFields
    }
  }

  ${userFragments['user']}
`;

export const fetchAdminUnitQuery = gql`
  query FetchAdminUnitQuery($cuid: String) {
    fetchAdminUnits(cuid: $cuid) {
      ...AdminUnitDetailFields
    }
  }
  ${adminUnitFragments.adminUnit}
`;

export const FetchAdminUnitsOverviewQuery = gql`
  query FetchAdminUnitsOverviewQuery($cuid: String) {
    fetchAdminUnits(cuid: $cuid) {
      ...AdminUnitFields
    }
  }
  ${adminUnitFragments.adminUnitOverview}
`;

export const fetchAdminUnitsEANOverviewQuery = gql`
  query FetchAdminUnitsEANOverviewQuery($cuid: String) {
    fetchAdminUnits(cuid: $cuid) {
      ...AdminUnitFields
      identities {cuid label created_at ean ean_type}
    }
  }
  ${adminUnitFragments.adminUnitOverview}
`;

export const fetchAdminUnitWithStocksQuery = gql`
  query FetchAdminUnitWithStocksQuery($cuid: String) {
    fetchAdminUnits(cuid: $cuid) {
      ...AdminUnitDetailFields
      stocks(only_critical: true) {
        ...StockDetailFields
      }
    }
  }
  ${adminUnitFragments.adminUnit}
  ${stockFragments.stock}
`;

export const fetchSubAdminUnitsQuery = gql`
  query FetchSubAdminUnitsQuery($parentAdminUnitCuid: String!) {
    subAdminUnits(parent_admin_unit_cuid: $parentAdminUnitCuid) {
      ...AdminUnitDetailFields
    }
  }
  ${adminUnitFragments.adminUnit}
`;

export const fetchSubAdminUnitsWithStocksQuery = gql`
  query FetchSubAdminUnitsWithStocksQuery($parentAdminUnitCuid: String!, $onlyCritical: Boolean = false) {
    subAdminUnits(parent_admin_unit_cuid: $parentAdminUnitCuid) {
      ...AdminUnitDetailFields
      stocks(only_critical: $onlyCritical) {
        ...StockDetailFields
      }
    }
  }
  ${adminUnitFragments.adminUnit}
  ${stockFragments.stock}
`;


export interface FetchAdminUnitsResult {
  fetchAdminUnits: AdministrationUnit[]
}

export interface FetchAdminUnitResult {
  fetchAdminUnit: AdministrationUnit
}

export interface FetchSubAdminUnitsResult {
  subAdminUnits: AdministrationUnit[]
}

@Injectable()
export class AdministrationUnitService extends VisbosService {

  @select(['session', 'user', 'cuid'])
  userCuid$: Observable<string>;


  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  public getAdminUnitsForCurrentUser(): ApolloQueryObservable<AdministrationUnit[]> {

    let data$ = this.fetchItems({
      query: fetchUserQuery,
      variables: {
        cuid: this.userCuid$
      }
    });

    return <ApolloQueryObservable<AdministrationUnit[]>>data$.pipe(map(({data}: any) => data.fetchUsers[0].administration_units));
  }

  public getAdminUnitDetails(cuid?: string | Observable<string>): ApolloQueryObservable<FetchAdminUnitsResult> {

    let result = this.fetchItems<FetchAdminUnitsResult>({
      query: fetchAdminUnitQuery,
      variables: {
        cuid: cuid || this.activeAdminUnitCuid$
      }
    });
    return result;

  }

  public getAdminUnits(): ApolloQueryObservable<FetchAdminUnitsResult> {
    return this.fetchItems<FetchAdminUnitsResult>({
      query: FetchAdminUnitsOverviewQuery
    });
  }

  public getAdminUnitsWithEAN(): ApolloQueryObservable<FetchAdminUnitsEANOverviewQueryQuery> {
    return this.fetchItems<FetchAdminUnitsEANOverviewQueryQuery>({
      query: fetchAdminUnitsEANOverviewQuery
    });
  }

  public getAdminUnitWithCriticalStocks(cuid?: string | Observable<string>): ApolloQueryObservable<FetchAdminUnitsResult> {
    let result = this.fetchItems<FetchAdminUnitsResult>({
      query: fetchAdminUnitWithStocksQuery,
      variables: {
        cuid: cuid || this.activeAdminUnitCuid$
      }
    });
    return result;
  }

  public getSubAdminUnits(parentAdminUnitCuid?: string): ApolloQueryObservable<FetchSubAdminUnitsResult> {
    return this.fetchItems<FetchSubAdminUnitsResult>({
      query: fetchSubAdminUnitsQuery,
      variables: {
        parentAdminUnitCuid: parentAdminUnitCuid || this.activeAdminUnitCuid$
      }
    });
  }

  public getSubAdminUnitsWithStocks(parentAdminUnitCuid?: string, onlyCritical: boolean = false): ApolloQueryObservable<FetchSubAdminUnitsResult> {
    return this.fetchItems<FetchSubAdminUnitsResult>({
      query: fetchSubAdminUnitsWithStocksQuery,
      variables: {
        parentAdminUnitCuid: parentAdminUnitCuid || this.activeAdminUnitCuid$,
        onlyCritical: onlyCritical
      }
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/organization/services/administration_unit_service/administration-unit.service.ts